/*
 * <size-guide-trigger>
 *
 * Opens the existing Kiwi Size Chart & Recommender popup from anywhere on the
 * product page. Used by the "Size & Fit" accordion row and by the sticky
 * "Select size" popup, so both entry points open the same guide.
 *
 * This deliberately does NOT reimplement a size guide. It clicks whatever
 * element Kiwi already binds to, so the app stays the single source of truth.
 *
 * IMPORTANT — the one line to change if the guide does not open:
 * KIWI_TRIGGER_SELECTORS below is the list of candidates we click, in order.
 * The values come from what this theme already references: `#sizeGuideOpen`
 * and `.size-guide-trigger` (see the Kiwi adaptor in sections/product.liquid)
 * plus the Kiwi app block container. If Kiwi is configured in the app admin
 * with a different custom trigger selector, add it as the FIRST entry.
 *
 * If no trigger is found on the page, the host element hides itself, matching
 * how the existing adaptor hides its triggers when Kiwi is absent.
 */

const KIWI_TRIGGER_SELECTORS = [
  '.size-guide-trigger',
  '#sizeGuideOpen',
  '[id^="shopify-block-kiwi_size_chart_recommender"] button',
  '[id^="shopify-block-kiwi_size_chart_recommender"] a',
];

/* Kiwi renders its block asynchronously, so give it a moment before deciding
   the guide is genuinely unavailable. */
const AVAILABILITY_CHECK_DELAY = 2000;

if (!customElements.get('size-guide-trigger')) {
  customElements.define(
    'size-guide-trigger',
    class SizeGuideTrigger extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-size-guide-open]');
        if (!this.button) return;

        this.handleClick = this.handleClick.bind(this);
        this.button.addEventListener('click', this.handleClick);

        this.availabilityTimer = setTimeout(() => {
          if (!this.findTarget()) this.hidden = true;
        }, AVAILABILITY_CHECK_DELAY);
      }

      disconnectedCallback() {
        if (this.button) this.button.removeEventListener('click', this.handleClick);
        clearTimeout(this.availabilityTimer);
      }

      /* Resolved at click time rather than cached, because Kiwi can inject its
         markup after this component connects. */
      findTarget() {
        for (const selector of KIWI_TRIGGER_SELECTORS) {
          const candidate = document.querySelector(selector);
          if (candidate && candidate !== this.button && !this.contains(candidate)) {
            return candidate;
          }
        }
        return null;
      }

      handleClick(event) {
        event.preventDefault();

        const target = this.findTarget();
        if (!target) {
          this.hidden = true;
          return;
        }

        target.click();
      }
    }
  );
}
